import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Package, Boxes, AlertCircle, CheckCircle2, ClipboardList } from 'lucide-react';
import { useBranding } from '../context/BrandingContext';

const LoginPage = () => {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const appName = branding?.app_name || 'BOM Maestro';
  const appTagline = branding?.tagline || 'Inventory & Production System';
  const heroImage = branding?.hero_data || '';
  const heroTagline = branding?.hero_tagline || 'Precision Inventory\n& BOM Management';
  const brandLogo = branding?.logo_data || '';
  const primary = branding?.primary_color || '#ea580c';
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingMsg, setPendingMsg] = useState(''); // iter52 — submitted-but-pending state

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register form state
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [registerRole, setRegisterRole] = useState('Operator');
  const [requestedRole, setRequestedRole] = useState('Operator');
  const [signupReason, setSignupReason] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(''); setPendingMsg('');
    try {
      await login(loginEmail, loginPassword);
      // iter120 — bounce back to the page they were on when the session expired.
      try {
        const dest = window.sessionStorage.getItem('post_login_redirect');
        if (dest) {
          navigate(dest, { replace: true });
          return;
        }
      } catch (_) {}
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(''); setPendingMsg('');
    try {
      // iter52: self-registration is always pending. We send the user's
      // requested role + reason as part of full_name's signup metadata so
      // the admin can see context — but cleanest is to append a small note.
      // The server forces role=Operator + approval_status=pending regardless.
      const fullNameWithMeta = signupReason
        ? `${registerName} — Requesting ${requestedRole}: ${signupReason}`
        : (requestedRole !== 'Operator'
            ? `${registerName} — Requesting ${requestedRole}`
            : registerName);
      await register(registerEmail, registerPassword, fullNameWithMeta, registerRole);
      setPendingMsg('Your account request has been submitted. An administrator will review it shortly — you will receive an email when access is granted.');
      // Reset form so they can't double-submit
      setRegisterEmail(''); setRegisterPassword(''); setRegisterName('');
      setRequestedRole('Operator'); setSignupReason('');
      setActiveTab('login');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-white p-12 flex-col justify-between relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("https://images.pexels.com/photos/4487363/pexels-photo-4487363.jpeg")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-8">
            {brandLogo ? (
              <div className="bg-white rounded-md py-2.5 px-4 inline-flex items-center" data-testid="login-brand-logo-pill">
                <img src={brandLogo} alt={appName} className="h-12 max-w-[200px] object-contain" data-testid="login-brand-logo" />
              </div>
            ) : (
              <>
                <div className="p-2 rounded-sm" style={{ background: primary }}>
                  <Boxes className="w-8 h-8" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight" data-testid="login-brand-name">{appName}</h1>
                  <p className="text-slate-400 text-sm">{appTagline}</p>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="relative z-10 space-y-8">
          {heroImage && (
            <img
              src={heroImage}
              alt="Hero"
              className="w-full max-w-md rounded-sm object-cover max-h-64"
              data-testid="login-hero-image"
            />
          )}
          <div>
            <h2 className="text-4xl font-bold leading-tight mb-4 whitespace-pre-line" data-testid="login-hero-tagline">
              {heroTagline}
            </h2>
            <p className="text-slate-300 text-lg max-w-md">
              Track components, manage production, and ensure complete traceability across your manufacturing operations.
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-800/50 p-4 rounded-sm border border-slate-700">
              <Package className="w-6 h-6 text-orange-500 mb-2" />
              <h3 className="font-semibold mb-1">Full Traceability</h3>
              <p className="text-slate-400 text-sm">Track every component from receipt to finished unit</p>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-sm border border-slate-700">
              <Boxes className="w-6 h-6 text-orange-500 mb-2" />
              <h3 className="font-semibold mb-1">BOM Control</h3>
              <p className="text-slate-400 text-sm">Manage revisions, variants, and production builds</p>
            </div>
          </div>
        </div>
        
        <div className="relative z-10 text-slate-500 text-sm">
          © {new Date().getFullYear()} {appName}. All rights reserved.
        </div>
      </div>

      {/* Right side - Auth forms */}
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="w-full max-w-md industrial-card">
          <CardHeader className="space-y-1">
            <div className="lg:hidden flex items-center gap-2 mb-4">
              <div className="p-1.5 bg-orange-500 rounded-sm">
                <Boxes className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg">{appName}</span>
            </div>
            <CardTitle className="text-2xl font-bold">
              {activeTab === 'login' ? 'Welcome back' : 'Create an account'}
            </CardTitle>
            <CardDescription>
              {activeTab === 'login' 
                ? 'Enter your credentials to access your account' 
                : 'Fill in your details to get started'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pendingMsg && (
              <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-sm flex items-start gap-2 text-emerald-800 text-sm" data-testid="register-pending-banner">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{pendingMsg}</span>
              </div>
            )}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm flex items-center gap-2 text-red-700 text-sm" data-testid="auth-error-banner">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="name@company.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      data-testid="login-email-input"
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="Enter your password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                      className="rounded-sm"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-slate-900 hover:bg-slate-800 rounded-sm"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? <span className="spinner" /> : 'Sign In'}
                  </Button>
                  <div className="text-center pt-1">
                    <Link to="/forgot-password" className="text-xs text-slate-500 hover:text-orange-600" data-testid="forgot-password-link">
                      Forgot your password?
                    </Link>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm flex items-start gap-2 text-amber-800 text-xs" data-testid="register-approval-notice">
                  <ClipboardList className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Account requests require admin approval.</strong> After you submit, you'll receive an email confirming when access is granted.
                  </span>
                </div>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Full Name</Label>
                    <Input
                      id="register-name"
                      type="text"
                      placeholder="John Doe"
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                      required
                      data-testid="register-name-input"
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Work Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="name@company.com"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      required
                      data-testid="register-email-input"
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="At least 8 characters"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      required
                      data-testid="register-password-input"
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-role">Requested Role <span className="text-xs text-slate-400">(admin will confirm)</span></Label>
                    <Select value={requestedRole} onValueChange={setRequestedRole}>
                      <SelectTrigger data-testid="register-role-select" className="rounded-sm">
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Admin">Admin</SelectItem>
                        <SelectItem value="InventoryManager">Inventory Manager</SelectItem>
                        <SelectItem value="ProductionSupervisor">Production Supervisor</SelectItem>
                        <SelectItem value="Purchasing">Purchasing</SelectItem>
                        <SelectItem value="Operator">Operator</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-reason">Reason for access <span className="text-xs text-slate-400">(optional)</span></Label>
                    <Input
                      id="register-reason"
                      type="text"
                      placeholder="e.g. New hire on the receiving team"
                      value={signupReason}
                      onChange={(e) => setSignupReason(e.target.value)}
                      data-testid="register-reason-input"
                      className="rounded-sm"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-slate-900 hover:bg-slate-800 rounded-sm"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? <span className="spinner" /> : 'Submit Access Request'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
